// Configuración simulada de aforo de la demo. Editar este fichero cambia el
// "guion" (plantas, zonas, aforos, curva del día) sin tocar código.
//
// Calibración: en el modo AFORO, Alt+clic sobre el modelo imprime en consola
// la posición del punto pulsado lista para pegar aquí.
//
// ---------------------------------------------------------------------------
// CÓMO DAR DE ALTA UNA PLANTA NUEVA (cuando el modelador la incorpore)
//
//  1. Acordar con el modelador el prefijo de nombre de los elementos de esa
//     planta en el FBX (p. ej. "--p2" o "psegunda"), igual que "pprimera" o
//     "--pbaja". Ese prefijo es `searchName`. Si el suelo tiene nombre propio,
//     añadirlo en `extraSearchNames` (en el GLB no hace falta: los suelos se
//     llaman "--Parking_Suelo", "--Pbaja_Suelo" y "--PPrimera_Suelo").
//  2. Añadir la planta a AFORO_AREAS con un `id` nuevo, su `nombre`, los
//     `aliases` con los que la gente la nombra ("segunda planta", "planta 2",
//     "P2"...), el `shadingGroup` (cualquier texto único) y las `vistas` que
//     quiere en el desplegable del módulo ("snapshot" basta para empezar).
//  3. Añadir sus zonas a AFORO_ZONES con `area` = ese id, su `tipo` (oficina,
//     comedor, sala...), aforo máximo y posición (rects calibrados o
//     `relative` al bbox de la planta mientras no haya calibración).
//
// Con eso la planta aparece en el módulo de aforo, en el heatmap, en el
// análisis y el avatar puede responder sobre ella. No hay que tocar código.
// ---------------------------------------------------------------------------

// Identificador de planta/área. Texto libre para que dar de alta una planta
// nueva sea solo configuración.
export type AforoAreaId = string;

// Vistas que una planta ofrece en el desplegable del módulo de aforo.
export type AforoViewKind = "analysis" | "replay" | "snapshot";

// Icono del desplegable (se traduce a un icono real en AforoButtons).
export type AforoAreaIcon = "oficinas" | "hall" | "parking" | "comedor";

export interface AforoArea {
    id: AforoAreaId;
    nombre: string;
    // Otras formas de nombrar la planta en una conversación.
    aliases: string[];
    // Prefijo de búsqueda por nombre en el modelo (aísla la planta y recibe
    // el sombreado).
    searchName: string;
    // Búsquedas adicionales que también forman parte del área (p. ej. el
    // suelo del parking, que tiene nombre propio en el modelo).
    extraSearchNames?: string[];
    shadingGroup: string;
    // Radio y exponente del sombreador: por defecto los globales
    // (AFORO_HEATMAP_CONFIDENCE / AFORO_HEATMAP_POWER); solo si una planta necesita otros.
    confidence?: number;
    powerParameter?: number;
    // Huella de la planta en el modelo (esquina mínima y tamaño en x/z): la
    // calcula el visor al cargar a partir de los elementos de la búsqueda.
    // La usan los planos esquemáticos (informe de eficiencia).
    bbox?: AforoRect;
    // Vistas del desplegable para esta planta. Por defecto "analysis" (análisis
    // de ocupación con fecha, hora, gráfica, replay e informe), que es el
    // comportamiento completo; "snapshot" es solo la foto con etiquetas.
    vistas?: AforoViewKind[];
    icono?: AforoAreaIcon;
    // Búsquedas por nombre de elementos que estorban en la vista cenital de
    // esta planta (rampas, escaleras, forjados superiores...) y se ocultan al
    // abrirla. Para saber el nombre de un elemento: Alt+clic sobre él en modo
    // AFORO lo imprime en consola.
    ocultar?: string[];
    // Tipo de sensor con el que se pinta la planta: "occupancy" (rampa de
    // presencia, por defecto) o "parking" (semáforo libre/ocupada por plaza).
    sensorType?: "occupancy" | "parking";
    // Leyenda propia (por defecto la de presencia).
    leyenda?: Array<{ ruleName: string; ruleColor: string }>;
    // Simulación de evacuación: dónde está la salida (coordenadas del modelo
    // o una zona cuya posición se usa como salida) y por qué zona de paso
    // circula la gente hasta ella (se calienta mientras evacúan).
    // Puede haber varias salidas (el hall tiene dos accesos): cada persona va
    // a la que le quede más cerca por su ruta.
    // Una salida es un punto, o la posición de una zona (p. ej. un acceso)
    // desplazada opcionalmente hacia otra zona (`haciaZona`, con `fraccion`
    // del camino) para ponerla en la puerta y no en el centro del acceso.
    salidas?: AforoPuntoRef[];
    transito?: string;
    // Espinas de las rutas de evacuación: una polilínea por salida, que
    // acaba en ella (el pasillo hasta la puerta; el carril del parking hasta
    // la rampa; el tramo de entrada a cada acceso del hall). Cada zona o
    // persona va en recta hasta el punto más cercano de la espina y la sigue
    // hasta la salida. Solo se puede entrar a la espina por sus tramos
    // intermedios, nunca por el último (el que ya es la salida), para que
    // nadie se salte el recorrido; si la espina es un único tramo, por él.
    // Sin espina, cada zona va en recta a la salida.
    espinas?: AforoPuntoRef[][];
}

// Un punto del plano: coordenadas del modelo, o la posición de una zona
// (p. ej. un acceso) desplazada opcionalmente hacia otra zona (`haciaZona`,
// con `fraccion` del camino).
export type AforoPuntoRef =
    | { x: number; z: number }
    | { zona: string; haciaZona?: string; fraccion?: number };

// Semáforo de plazas de parking: 0 = libre (verde), 0.5 = carril (neutro),
// 1 = ocupada (rojo). Con un exponente alto cada plaza sale de un color.
export const AFORO_PARKING_COLORS = [0x00b24a, 0xa9b8c6, 0xe60026];

export const AFORO_PARKING_LEGEND = [
    { ruleName: "Plaza libre", ruleColor: "#00B24A" },
    { ruleName: "Plaza ocupada", ruleColor: "#E60026" },
];

export const AFORO_PARKING_RESERVAS_LEGEND = [
    { ruleName: "Plaza libre", ruleColor: "#00B24A" },
    { ruleName: "Plaza reservada", ruleColor: "#E60026" },
];

// Vista de RESERVAS (independiente de la ocupación): cada espacio reservable
// se pinta según lo reservado en el tramo elegido. Valores del sombreado:
// 0 = no reservable (gris), 1/3 = libre (verde), 2/3 = parcialmente
// reservado (ámbar), 1 = completo (rojo).
export const AFORO_RESERVAS_COLORS = [0xa9b8c6, 0x00b24a, 0xffb000, 0xe60026];

export const AFORO_RESERVAS_LEGEND = [
    { ruleName: "No reservable", ruleColor: "#A9B8C6" },
    { ruleName: "Libre", ruleColor: "#00B24A" },
    { ruleName: "Parcialmente reservado", ruleColor: "#FFB000" },
    { ruleName: "Completo", ruleColor: "#E60026" },
];

// Vista de reservas sobre el mobiliario (plantas con `elementos` en sus
// zonas): semáforo por mesa, sala o despacho.
export const AFORO_RESERVAS_BLOQUES_LEGEND = [
    { ruleName: "Disponible", ruleColor: "#00B24A" },
    { ruleName: "Parcialmente reservado", ruleColor: "#FFB000" },
    { ruleName: "Reservado", ruleColor: "#E60026" },
];

/** Fracción reservada (0..1, o null si no es reservable) → valor del sombreado de reservas. */
export const reservasShadingValue = (ratio: number | null): number =>
    ratio === null ? 0 : 1 / 3 + (2 / 3) * Math.max(0, Math.min(1, ratio));

/** Color del chip de un espacio en la vista de reservas. */
export const reservasRatioToColor = (ratio: number): string => {
    if (ratio <= 0) return "#00B24A";
    if (ratio < 1) return "#FFB000";
    return "#E60026";
};

export const AFORO_AREAS: AforoArea[] = [
    {
        id: "primera",
        nombre: "Primera planta",
        aliases: ["planta 1", "planta primera", "P1", "primera", "oficinas", "arriba"],
        searchName: "pprimera",
        shadingGroup: "Aforo",
        icono: "oficinas",
        // Salida por la puerta del núcleo de escaleras ("--PPrimera_Puerta_001",
        // en x ≈ 11.7, z ≈ -16.1), en la esquina suroeste. La espina baja por
        // el pasillo (x ≈ 13.65, eje de sus luminarias) y gira a la puerta.
        salidas: [{ x: 11.9, z: -16.1 }],
        espinas: [
            [
                { x: 13.65, z: 16.5 },
                { x: 13.65, z: -16.1 },
                { x: 11.9, z: -16.1 },
            ],
        ],
        transito: "pasillo",
    },
    {
        id: "baja",
        nombre: "Planta baja",
        aliases: ["planta 0", "planta cero", "P0", "baja", "hall", "recepción", "entrada"],
        searchName: "--pbaja",
        shadingGroup: "AforoBaja",
        icono: "hall",
        // Dos salidas, los dos accesos: cada persona va a la más cercana.
        // Las salidas son los dos accesos. A cada uno se entra por un tramo
        // recto desde el hall (un tercio del camino entre el hall y el
        // acceso) hasta el acceso: la gente va en recta hasta ese tramo y
        // baja por él hasta la salida.
        salidas: [{ zona: "bajaAcceso1" }, { zona: "bajaAcceso2" }],
        espinas: [
            [
                { zona: "bajaAcceso1", haciaZona: "bajaHall", fraccion: 0.35 },
                { zona: "bajaAcceso1" },
            ],
            [
                { zona: "bajaAcceso2", haciaZona: "bajaHall", fraccion: 0.35 },
                { zona: "bajaAcceso2" },
            ],
        ],
        transito: "bajaHall",
    },
    {
        id: "parking",
        nombre: "Parking",
        aliases: ["aparcamiento", "garaje", "planta -1", "sótano", "P-1"],
        searchName: "parking",
        // "--Parking_Suelo" ya casa con "parking"; la rampa se excluye en
        // searchExclusions para no deformar la huella.
        shadingGroup: "AforoParking",
        icono: "parking",
        // Salida por la rampa ("--Rampa_Parking"), que entra por el muro oeste
        // (x 6.4) entre z 7.8 y 15.3, pasadas las barreras. La espina recorre
        // el carril central (x = 16) y, a la altura de la rampa, gira hacia
        // ella: cada fila de plazas se une a esa línea en perpendicular.
        salidas: [{ x: 6.6, z: 11.5 }],
        espinas: [
            [
                { x: 16, z: -16.5 },
                { x: 16, z: 11.5 },
                { x: 6.6, z: 11.5 },
            ],
        ],
        transito: "parkingCirculacion",
        // Semáforo por plaza con mapa de calor: seis sensores por plaza y, A
        // PROPÓSITO, radio corto y exponente 2. Con el radio corto el tramado del
        // sombreador dibuja los círculos dentro de cada plaza (el aspecto que
        // se aprobó); con exponente mayor que 2 el sombreador se va a un solo
        // color (ver AFORO_HEATMAP_POWER). No tocar sin comparar en pantalla.
        sensorType: "parking",
        confidence: 10,
        powerParameter: 2,
        leyenda: AFORO_PARKING_LEGEND,
    },
];

/**
 * Centros de las plazas de una zona de parking: la lista tal cual, o la
 * primera y la última de una fila con las intermedias interpoladas.
 */
export const expandPlazas = (zone: AforoZone): Array<{ x: number; z: number }> => {
    if (!zone.plazas) return [];
    if (Array.isArray(zone.plazas)) return zone.plazas;
    const { desde, hasta, n } = zone.plazas;
    const plazas: Array<{ x: number; z: number }> = [];
    for (let k = 0; k < n; k += 1) {
        const t = n > 1 ? k / (n - 1) : 0;
        plazas.push({ x: desde.x + (hasta.x - desde.x) * t, z: desde.z + (hasta.z - desde.z) * t });
    }
    return plazas;
};

// Tipo de espacio. Sirve para que el avatar entienda "el comedor", "una sala"
// o "un puesto" y, más adelante, para las reglas de reserva de cada tipo.
export type AforoZoneType =
    | "despacho"
    | "sala"
    | "trabajo"
    | "oficina"
    | "comedor"
    | "hall"
    | "acceso"
    | "recepcion"
    | "parking"
    | "circulacion";

// Qué se reserva en una zona (preparado para la fase de reservas por voz):
// "puesto" = plazas individuales dentro de la zona, "sala" = la zona entera,
// "turno" = franjas (comedor). Sin valor = no reservable.
export type AforoReservable = "puesto" | "sala" | "turno";

// Nombres por los que la gente llama a cada tipo de espacio.
export const AFORO_ZONE_TYPE_ALIASES: { [tipo in AforoZoneType]: string[] } = {
    despacho: ["despacho", "despachos", "oficina individual"],
    sala: ["sala", "salas", "sala de reuniones", "reuniones", "meeting"],
    trabajo: ["zona de trabajo", "zonas de trabajo", "puestos", "open space", "espacio abierto"],
    oficina: ["oficina", "oficinas", "loomer", "loomers"],
    comedor: ["comedor", "comedores", "cantina", "cafetería", "restaurante"],
    hall: ["hall", "vestíbulo", "lobby", "entrada"],
    acceso: ["acceso", "accesos", "torno", "tornos", "puerta"],
    recepcion: ["recepción", "mostrador"],
    parking: ["parking", "aparcamiento", "garaje", "plazas", "coches"],
    circulacion: ["pasillo", "pasillos", "circulación"],
};

// Rectángulo de una zona: centro (x, z) y dimensiones en unidades del modelo.
export interface AforoRect {
    x: number;
    z: number;
    w: number;
    d: number;
}

export interface AforoZone {
    id: string; // sin guiones bajos (el sufijo de punto de malla usa "_")
    nombre: string;
    // Tipo de espacio (ver AforoZoneType): el avatar lo usa para entender
    // "el comedor" o "una sala" y las reservas para saber qué se reserva.
    tipo: AforoZoneType;
    // Otras formas de nombrar la zona en una conversación.
    aliases?: string[];
    // Qué se reserva aquí (fase de reservas por voz). Sin valor = no reservable.
    reservable?: AforoReservable;
    // Solo etiqueta con el nombre sobre el plano: sin aforo, sin sensores y
    // fuera de gráficas, informes y respuestas del avatar (accesos, mostrador).
    soloEtiqueta?: boolean;
    // Mobiliario de la zona en el modelo, para la vista de reservas (se tiñe
    // verde, ámbar o rojo como un semáforo). Prefijo del nombre de los
    // elementos (Alt+clic en modo AFORO lo imprime), o "auto" para coger los
    // elementos que caen enteros dentro de los rectángulos de la zona.
    // En las zonas que se reservan por puestos, cada elemento es un bloque
    // (una mesa con sus sillas) con nombre propio para reservar hablando:
    // "Mesa 1", "Mesa 2"... (`bloqueNombre`), numerados de seguido dentro de
    // la planta en el orden de las zonas y, dentro de cada zona, por posición.
    elementos?: string;
    bloqueNombre?: string;
    // Plazas individuales (parking): cada una es un sensor con dos estados,
    // libre u ocupada, y el aforo máximo es su número. Se dan como lista de
    // centros o como primera y última plaza de una fila (se interpolan).
    plazas?:
        | Array<{ x: number; z: number }>
        | { desde: { x: number; z: number }; hasta: { x: number; z: number }; n: number };
    // Tamaño de cada plaza (unidades del modelo): `plazaAncho` a lo largo de
    // la fila (eje Z) y `plazaFondo` en profundidad (eje X). Con tamaño, cada
    // plaza lleva varios sensores repartidos por su rectángulo y se pinta
    // como un bloque; sin tamaño, un sensor en el centro (mancha redonda).
    plazaAncho?: number;
    plazaFondo?: number;
    // Otras zonas cuyas cifras se suman a las de esta en el chip y en las
    // respuestas del avatar (p. ej. la zona B incluye las plazas eléctricas).
    agrupa?: string[];
    // Zona de relleno con valor fijo en el heatmap (0..1), p. ej. el carril
    // del parking en neutro (0.5) para que no herede el color de las plazas.
    valorFijo?: number;
    aforoMaximo: number;
    // Sesgo de ocupación de la zona respecto a la curva global del edificio
    // (1 = media; >1 se llena antes/más; <1 zona tranquila).
    bias: number;
    area: AforoAreaId;
    // Zona de relleno sin etiqueta ni presencia en gráficas/informes (p. ej.
    // la circulación del parking): solo pinta el suelo en el heatmap.
    hidden?: boolean;
    // Ancla del label (y también origen del rect por defecto). Para las zonas
    // "relative" se resuelve sola al cargar.
    position?: { x: number; y: number; z: number };
    // Rectángulos que ocupan la zona (una L = dos rects). Sobre ellos se
    // genera la malla de sensores que rellena la zona de color.
    rects?: AforoRect[];
    // Alternativa relativa al bounding box del área (fracciones 0..1):
    // centro (u, v) y tamaño (ru, rv). Útil mientras no haya calibración.
    relative?: { u: number; v: number; ru?: number; rv?: number };
}

// Cota del suelo de la primera planta: cara superior de "--PPrimera_Suelo".
// El modelo (GLB Parte019) va en METROS, Y arriba. Todas las coordenadas de
// este fichero son del visor (el modelo ya centrado), las que imprime Alt+clic
// en modo AFORO.
const Y_FLOOR = -6.4;

// Primera planta en el GLB: ala oeste x 6.5..11.9 (despachos y salas), pasillo
// x 11.9..15.5 (luminarias en x ≈ 13.65), núcleo x 15.5..20.2 (con dos salas
// de mesa grande) y banda este x 20.2..25.6 junto a la fachada (mesas de
// trabajo). Las plantas van apiladas: no hay desplazamiento entre ellas.

export const AFORO_ZONES: AforoZone[] = [
    // ---- Primera planta (GLB Parte019): 3 despachos individuales y 2 salas
    // en el ala oeste (x 6.5..11.9), pasillo (x 11.9..15.5), dos salas de mesa
    // grande en el núcleo (x 15.5..20.2) y la banda de mesas de trabajo junto
    // a la fachada (x 20.2..25.6). Rectángulos deducidos del mobiliario del
    // modelo (--PPrimera_Mesa_*, --PPrimera_Silla_*): si una zona queda
    // torcida, Alt+clic en modo AFORO imprime la posición real ----
    {
        id: "despacho1",
        nombre: "Despacho 1",
        tipo: "despacho",
        reservable: "sala",
        elementos: "auto",
        aforoMaximo: 1,
        bias: 0.9,
        area: "primera",
        // Mesa_009 (z -7.7..-5.9) con su silla.
        position: { x: 9.2, y: Y_FLOOR, z: -6.85 },
        rects: [{ x: 9.2, z: -6.85, w: 4.6, d: 5.4 }],
    },
    {
        id: "despacho2",
        nombre: "Despacho 2",
        tipo: "despacho",
        reservable: "sala",
        elementos: "auto",
        aforoMaximo: 1,
        bias: 0.85,
        area: "primera",
        // Mesa_008 (z -2.2..-0.4).
        position: { x: 9.2, y: Y_FLOOR, z: -1.15 },
        rects: [{ x: 9.2, z: -1.15, w: 4.6, d: 5.4 }],
    },
    {
        id: "despacho3",
        nombre: "Despacho 3",
        tipo: "despacho",
        reservable: "sala",
        elementos: "auto",
        aforoMaximo: 1,
        bias: 0.9,
        area: "primera",
        // Mesa_007 (z 3..4.8).
        position: { x: 9.2, y: Y_FLOOR, z: 4.55 },
        rects: [{ x: 9.2, z: 4.55, w: 4.6, d: 5.4 }],
    },
    {
        id: "salaPequena1",
        nombre: "Sala de reuniones 1",
        tipo: "sala",
        elementos: "auto",
        reservable: "sala",
        aforoMaximo: 6,
        bias: 1.2,
        area: "primera",
        // Mesa_011 (z 7.6..11) con 8 sillas.
        position: { x: 9.2, y: Y_FLOOR, z: 9.6 },
        rects: [{ x: 9.2, z: 9.6, w: 4.6, d: 4.6 }],
    },
    {
        id: "salaPequena2",
        nombre: "Sala de reuniones 2",
        tipo: "sala",
        elementos: "auto",
        reservable: "sala",
        aforoMaximo: 6,
        bias: 1.15,
        area: "primera",
        // Mesa_010 (z 12.8..16.2) con 8 sillas.
        position: { x: 9.2, y: Y_FLOOR, z: 14.4 },
        rects: [{ x: 9.2, z: 14.4, w: 4.6, d: 4.8 }],
    },
    {
        id: "pasillo",
        nombre: "Pasillo",
        tipo: "circulacion",
        aforoMaximo: 12,
        bias: 0.45,
        area: "primera",
        // De punta a punta de la planta entre el ala oeste y el núcleo; la
        // salida está en su extremo sur (puerta del núcleo de escaleras).
        position: { x: 13.65, y: Y_FLOOR, z: 0 },
        rects: [{ x: 13.65, z: 0, w: 3.4, d: 33.6 }],
    },
    {
        id: "trabajo1",
        nombre: "Zona de trabajo 1",
        tipo: "trabajo",
        reservable: "puesto",
        elementos: "--pprimera_mesa",
        bloqueNombre: "Mesa",
        aforoMaximo: 18,
        bias: 1.05,
        area: "primera",
        // Mitad sur: Mesa_001 y Mesa_002 junto a la fachada y Mesa_004 en el
        // núcleo, seis sillas cada una.
        position: { x: 23, y: Y_FLOOR, z: -8.5 },
        rects: [
            { x: 23, z: -8.5, w: 5, d: 16.4 },
            { x: 17.85, z: -4.9, w: 4.5, d: 7.6 },
        ],
    },
    {
        id: "trabajo2",
        nombre: "Zona de trabajo 2",
        tipo: "trabajo",
        reservable: "puesto",
        elementos: "--pprimera_mesa",
        bloqueNombre: "Mesa",
        aforoMaximo: 18,
        bias: 1,
        area: "primera",
        // Mitad norte: Mesa_003 y Mesa_006 junto a la fachada y Mesa_005 en el
        // núcleo, seis sillas cada una.
        position: { x: 23, y: Y_FLOOR, z: 8.5 },
        rects: [
            { x: 23, z: 8.5, w: 5, d: 16.4 },
            { x: 17.85, z: 4.2, w: 4.5, d: 7.6 },
        ],
    },

    // ---- Planta baja: el hall del edificio, con dos accesos y el mostrador
    // de recepción (posiciones relativas al bbox hasta calibrarlas) ----
    {
        // El hall ES la planta: su malla cubre casi todo el bbox para que el
        // suelo sature de color y no quede semitransparencia rayada.
        id: "bajaHall",
        nombre: "Hall",
        tipo: "hall",
        aforoMaximo: 40,
        bias: 1,
        area: "baja",
        relative: { u: 0.5, v: 0.5, ru: 0.92, rv: 0.92 },
    },
    {
        // Acceso superior derecho (según anotaciones del usuario).
        id: "bajaAcceso1",
        nombre: "Acceso 1",
        tipo: "acceso",
        soloEtiqueta: true,
        aforoMaximo: 8,
        bias: 0.6,
        area: "baja",
        relative: { u: 0.83, v: 0.16, ru: 0.16, rv: 0.14 },
    },
    {
        // Acceso inferior derecho.
        id: "bajaAcceso2",
        nombre: "Acceso 2",
        tipo: "acceso",
        soloEtiqueta: true,
        aforoMaximo: 8,
        bias: 0.55,
        area: "baja",
        relative: { u: 0.78, v: 0.83, ru: 0.16, rv: 0.14 },
    },
    {
        // Mostrador curvo del lado izquierdo.
        id: "bajaRecepcion",
        nombre: "Mostrador de recepción",
        tipo: "recepcion",
        soloEtiqueta: true,
        aforoMaximo: 6,
        bias: 0.85,
        area: "baja",
        relative: { u: 0.2, v: 0.47, ru: 0.18, rv: 0.38 },
    },

    // ---- Parking (GLB Parte019): dos filas de plazas con el carril en medio
    // (x 12.1..19.9, entre las líneas de pilares y extintores). Fila A junto
    // al muro oeste (x 6.3..12.1, centro 9.1), fila B junto al este (x
    // 19.9..25.6, centro 22.9); las 2 eléctricas, al sur de la fila B, delante
    // de los cargadores (--Parking_CargElec_*). Entrada por la rampa, al oeste
    // (z 7.8..15.3), con las barreras. Plazas trasladadas de la calibración
    // del 015 (la planta es la misma); la "circulación" es relleno oculto en
    // neutro para que el carril no herede el color de las plazas ----
    {
        id: "parkingCirculacion",
        nombre: "Circulación",
        tipo: "circulacion",
        aforoMaximo: 10,
        bias: 0.25,
        area: "parking",
        hidden: true,
        valorFijo: 0.5,
        // Carril central, lado norte (entrada desde la rampa) y rincón suroeste
        // (explícitos, para no meter sensores neutros dentro de las plazas).
        rects: [
            { x: 16, z: 0, w: 7.8, d: 34 },
            { x: 16, z: 13.6, w: 19.2, d: 6.8 },
            { x: 9.2, z: -15, w: 5.8, d: 4 },
        ],
    },
    {
        id: "parkingA",
        nombre: "Parking zona A",
        tipo: "parking",
        reservable: "puesto",
        aforoMaximo: 7,
        bias: 1.1,
        area: "parking",
        plazaAncho: 2.9,
        plazaFondo: 5.4,
        plazas: [
            { x: 9.24, z: 6.15 },
            { x: 9.19, z: 3.2 },
            { x: 9.24, z: 0.24 },
            { x: 9.14, z: -2.56 },
            { x: 9.14, z: -5.51 },
            { x: 9.09, z: -8.52 },
            { x: 9.04, z: -11.42 },
        ],
    },
    {
        id: "parkingB",
        nombre: "Parking zona B",
        tipo: "parking",
        reservable: "puesto",
        aforoMaximo: 7,
        bias: 1.05,
        area: "parking",
        plazaAncho: 2.9,
        plazaFondo: 5.4,
        plazas: [
            { x: 23.02, z: 8.63 },
            { x: 22.92, z: 5.83 },
            { x: 22.97, z: 2.93 },
            { x: 23.07, z: -0.17 },
            { x: 22.92, z: -2.86 },
            { x: 22.92, z: -6.23 },
            { x: 22.97, z: -8.82 },
        ],
    },
    {
        id: "parkingElectricos",
        nombre: "Parking eléctricos",
        tipo: "parking",
        aliases: ["cargadores", "plazas eléctricas", "coches eléctricos", "plazas con cargador"],
        reservable: "puesto",
        aforoMaximo: 2,
        bias: 1.15,
        area: "parking",
        plazaAncho: 2.9,
        plazaFondo: 5.4,
        plazas: [
            { x: 22.92, z: -12.08 },
            { x: 22.87, z: -15.03 },
        ],
    },
];

// ---------------------------------------------------------------------------
// Mapa de calor por PERSONAS SIMULADAS. Cada zona tiene "asientos": posiciones
// fijas donde puede haber una persona (una malla dentro de sus rectángulos).
// La fecha y la hora deciden cuántos están ocupados y cuáles. En el sombreado
// cada persona es un foco caliente (valor 1) y una malla de sensores fríos
// (valor 0) cubre el suelo: lejos de la gente todo queda azul, alrededor de
// cada persona sale un foco y donde se juntan varias, una mancha roja.
// ---------------------------------------------------------------------------

// Malla de sensores FRÍOS por zona: separación objetivo entre puntos y
// desorden (fracción de celda) para que el fondo no se vea regular.
export const AFORO_GRID_SPACING = 2.6;
export const AFORO_GRID_JITTER = 0.2;

// Asientos por zona: separación entre posiciones posibles de persona y
// cuántas se generan respecto al aforo máximo (margen para "aforo superado").
export const AFORO_SEAT_SPACING = 1.2;
export const AFORO_SEAT_EXTRA = 1.3;

// Estilo del mapa de calor de aforo:
//  - "difuso": pocos focos por zona con el nivel de ocupación de la zona,
//    fundidos por toda la planta como los mapas de humedad y temperatura.
//  - "personas": malla fría más un foco por persona simulada (asientos).
export const AFORO_HEATMAP_STYLE: "difuso" | "personas" = "difuso";

// Estilo "difuso": separación entre focos dentro de cada zona (una zona
// pequeña tiene un foco en el centro; una grande, varios repartidos).
export const AFORO_FOCUS_SPACING = 7;

// Radio del sombreador de Autodesk. Lejos del sensor más cercano respecto a
// este radio, el sombreador mezcla un tramado gris ("circulitos"/rayado): con
// un radio enorme el tramado desaparece y el color es el de la interpolación.
export const AFORO_HEATMAP_CONFIDENCE = 10_000;
// Exponente de la interpolación por distancia. NO SUBIR DE 2: el sombreador de
// Autodesk suma 0.000001 al divisor de su media ponderada (1/d^p) y, con el
// modelo en centímetros, a partir de p = 3 los pesos reales quedan por debajo
// de ese valor y todo el mapa se va al color del valor 0. Para estados nítidos
// por elemento (plazas de parking) se usan rectángulos, no mapa de calor.
export const AFORO_HEATMAP_POWER = 2;

// Simulación de evacuación (ver aforoEvacuation.ts). Unidades del modelo (m).
export const AFORO_EVACUACION = {
    // Velocidad de marcha (m/s) con gente alrededor; cada persona varía ±15 %.
    velocidad: 1,
    // Segundos de reacción desde la alarma hasta que la gente empieza a salir
    // (recoger, mirar alrededor...), y margen de dispersión entre personas.
    retardo: 5,
    dispersionSalida: 5,
    // Personas por segundo que salen de cada zona (su puerta) y que pasan por
    // la salida de la planta (una puerta normal ronda una por segundo; la
    // cola se forma ahí).
    caudalZona: 0.7,
    caudalSalida: 1.1,
    // Objetivo de evacuación de la planta (segundos) con el que se compara.
    objetivo: 180,
    // Segundos simulados por cada segundo real al verlo en el plano.
    aceleracion: 3,
    // Dibujo de las rutas y de la gente: ancho de la cinta, paso entre
    // flechas, radio del punto de cada persona y separación en la cola (m).
    anchoRuta: 0.28,
    pasoFlechas: 6,
    radioPersona: 0.17,
    separacionCola: 0.55,
};

// Horario simulado del edificio: fuera de él la ocupación es cero. Es también
// el tramo que recorre el replay y el eje de las gráficas.
export const AFORO_HORARIO = { inicio: 7, fin: 22 };

// Hora que el avatar considera "ahora" al responder sobre ocupación en vivo.
//  - "real": la hora del reloj del navegador (la local del visitante, no la
//    del servidor).
//  - "fija": siempre la hora indicada (útil en demos fuera de horario).
export const AFORO_HORA_EN_VIVO: { modo: "real" | "fija"; hora: number } = {
    modo: "real",
    hora: 11.5,
};

// Curva de ocupación global del edificio a lo largo del día [hora, factor 0..1].
// Se interpola linealmente entre puntos.
export const AFORO_DAY_CURVE: Array<[number, number]> = [
    [7, 0],
    [8, 0.15],
    [9, 0.55],
    [10.5, 0.85],
    [13, 0.7],
    [14.5, 0.35],
    [16, 0.75],
    [18, 0.55],
    [19.5, 0.35],
    [21, 0.15],
    [22, 0],
];

// Rampa de densidad del heatmap (frío → caliente) y su leyenda. Los chips
// "n/aforo" de cada zona siguen usando el semáforo de aforoRatioToColor.
export const AFORO_HEATMAP_COLORS = [0x1f3fbf, 0x1fb4d8, 0x39c66b, 0xffd500, 0xe60026];

export const AFORO_LEGEND = [
    { ruleName: "Sin presencia", ruleColor: "#1F3FBF" },
    { ruleName: "Poca gente", ruleColor: "#1FB4D8" },
    { ruleName: "Presencia media", ruleColor: "#39C66B" },
    { ruleName: "Mucha gente", ruleColor: "#FFD500" },
    { ruleName: "Aglomeración", ruleColor: "#E60026" },
];

// Color del chip de cada zona según su ratio de ocupación.
export const aforoRatioToColor = (ratio: number): string => {
    if (ratio < 0.4) return "#00B24A";
    if (ratio < 0.6) return "#8BD100";
    if (ratio < 0.8) return "#FFD500";
    if (ratio < 1) return "#FF8000";
    return "#E60026";
};
